import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { useState } from 'react';
import { CommonDialog, CommonDialogHeader } from '..';
import { useForm } from 'react-hook-form';
import { ICreateCustomerSettingRequest } from '../../../types/api/request/customersettingsrequest.d';
import { CustomerSettingsService } from '../../../api/services/customerSettingsService';
import { useDispatch, useSelector } from 'react-redux';
import { addCustomerSetting } from '../../../store/Reducers/CustomerSettingSlice';
import { IRootState } from '../../../types/store';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IAddCustomerSettingDialogBodyProps } from '../../../types/dialog';

export const AddCustomerSettingDialog = () => {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const dispatch = useDispatch();

  const onSubmit = async (payload: ICreateCustomerSettingRequest) => {
    try {
      const setting = await CustomerSettingsService.createCustomerSetting(payload);
      dispatch(addCustomerSetting(setting));
      dispatch(
        showNotification({
          message: 'Customer setting successfully created',
        }),
      );
      setIsOpenDialog(false);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: `Failed to add customer setting. ${error?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
        onClick={() => {
          setIsOpenDialog(true);
        }}
      >
        Add Setting
      </Button>
      {isOpenDialog ? (
        <CommonDialog
          isOpen={isOpenDialog}
          header={
            <CommonDialogHeader
              title={'Add Customer Setting'}
              onClose={() => {
                setIsOpenDialog(false);
              }}
            />
          }
          body={<DialogBody onSubmit={onSubmit} />}
          footer={undefined}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const DialogBody = ({ onSubmit }: IAddCustomerSettingDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ICreateCustomerSettingRequest>({
    defaultValues: {
      metakey: '',
      metavalue: '',
      description: '',
    },
    mode: 'onChange',
  });

  const { content: customerSettings } = useSelector((state: IRootState) => state.customerSettings);

  const handleFormSubmit = async (data: ICreateCustomerSettingRequest) => {
    try {
      await onSubmit(data); // call parent onSubmit
      reset(); // reset form fields after success
    } catch {}
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        <FormControl>
          <FormLabel>Meta Key</FormLabel>
          <TextField
            type="text"
            placeholder="operations_start_time"
            fullWidth
            variant="outlined"
            error={!!errors.metakey}
            helperText={errors.metakey?.message}
            {...register('metakey', {
              required: 'Meta key is required',
              validate: (value) => {
                const keyExists = customerSettings?.some(
                  (s) => s.metakey.toLowerCase() === value.trim().toLowerCase(),
                );
                return keyExists ? 'A setting with this metakey already exists' : true;
              },
            })}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Meta Value</FormLabel>
          <TextField
            type="text"
            placeholder="Value"
            fullWidth
            variant="outlined"
            error={!!errors.metavalue}
            helperText={errors.metavalue?.message}
            {...register('metavalue', {
              required: 'Meta value is required',
            })}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <TextField
            type="text"
            placeholder="What this setting controls..."
            fullWidth
            variant="outlined"
            error={!!errors.description}
            helperText={errors.description?.message}
            {...register('description')}
          />
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ backgroundColor: '#000' }}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
      </Box>
    </>
  );
};
